import React, { useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import Breadcrumb from '../components/Breadcrumb';
import axios from '../api';
import { resolveImageUrl } from '../utils/imageUrl';
import {
  Box,
  Typography,
  Card,
  CardContent,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Button,
  Popover,
  TextField,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import TagIcon from '@mui/icons-material/Tag';
import CategoryIcon from '@mui/icons-material/Category';
import DescriptionIcon from '@mui/icons-material/Description';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import EditIcon from '@mui/icons-material/Edit';
import { useToast } from '../components/ToastProvider';
import { useUser } from '../components/UserContext';
import ProductForm from '../components/ProductForm';
import { formatDate as formatDisplayDate } from '../utils/dateUtils';
import PageLoader from '../components/ui/PageLoader';
import PageHeader from '../components/ui/PageHeader';
import { pageContainerSx } from '../styles/tokens';

const STAT_CARDS = [
  { key: 'total_stock', label: 'Total Stock', icon: Inventory2OutlinedIcon },
  { key: 'booked_this_month', label: 'Total Quantity Booked ', icon: EventAvailableIcon },
  { key: 'utilization', label: 'Utilization', suffix: '%', icon: TrendingUpIcon },
  { key: 'active_customers', label: 'Active Customers', icon: PeopleOutlineIcon },
];

function InfoRow({ icon: Icon, label, value }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
      <Icon sx={{ color: 'text.secondary', fontSize: 20, mt: 0.25 }} />
      <Typography variant="body2" sx={{ color: 'text.primary' }}>
        <Box component="span" sx={{ fontWeight: 600 }}>{label}: </Box>
        <Box component="span" sx={{ color: 'text.secondary' }}>{value || '—'}</Box>
      </Typography>
    </Box>
  );
}

const formatDateRangeLabel = (from, to) => {
  if (!from || !to) return 'Pick a date range';
  const f = new Date(from);
  const t = new Date(to);
  const opts = { day: '2-digit', month: 'short', year: 'numeric' };
  return `${f.toLocaleDateString('en-GB', opts)} – ${t.toLocaleDateString('en-GB', opts)}`;
};

const ProductDetails = () => {
  const { id } = useParams();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const productsReturnPath = location.state?.fromProducts || '/products';
  const [product, setProduct] = useState(null);
  const [stats, setStats] = useState(null);
  const [calendarData, setCalendarData] = useState({});
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() };
  });
  const [startDate, setStartDate] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
  });
  const [endDate, setEndDate] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10);
  });
  const [dateAnchor, setDateAnchor] = useState(null);
  const [bookingHistory, setBookingHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const showToast = useToast();
  const { user } = useUser();
  const isWarehouse = (user?.role || '').toString().toLowerCase() === 'warehouse';

  const openEditModal = () => setEditModalOpen(true);
  const closeEditModal = () => {
    setEditModalOpen(false);
    fetchProduct();
    fetchStats();
    fetchBookingHistory();
  };

  const fetchProduct = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/products');
      const found = res.data.find(p => p.id === parseInt(id));
      setProduct(found);
    } catch (err) {
      showToast('Error fetching product', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    if (!id) return;
    try {
      const res = await axios.get(`/api/products/${id}/stats`, {
        params: { start_date: startDate, end_date: endDate },
      });
      setStats(res.data);
    } catch (err) {
      setStats({ total_stock: 0, booked_this_month: 0, utilization: 0, active_customers: 0 });
    }
  };

  const fetchBookingHistory = async () => {
    if (!id) return;
    try {
      const res = await axios.get(`/api/products/${id}/bookings/history`, {
        params: { start_date: startDate, end_date: endDate },
      });
      setBookingHistory(res.data || []);
    } catch (err) {
      showToast('Error fetching booking history', 'error');
      setBookingHistory([]);
    }
  };

  const toLocalDateStr = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const fetchCalendar = async () => {
    if (!id) return;
    const start = new Date(calendarMonth.year, calendarMonth.month, 1);
    const end = new Date(calendarMonth.year, calendarMonth.month + 1, 0);
    const startStr = toLocalDateStr(start);
    const endStr = toLocalDateStr(end);
    try {
      const res = await axios.get(`/api/products/${id}/calendar`, {
        params: { start: startStr, end: endStr },
      });
      setCalendarData(res.data);
    } catch (err) {
      setCalendarData({});
    }
  };

  useEffect(() => {
    fetchProduct();
  }, [id]);

  useEffect(() => {
    if (isWarehouse) return;
    fetchStats();
    fetchBookingHistory();
  }, [id, startDate, endDate, isWarehouse]);

  useEffect(() => {
    fetchCalendar();
  }, [id, calendarMonth.year, calendarMonth.month]);

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const dayLabels = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
  const getDaysInMonth = (year, month) => {
    const first = new Date(year, month, 1);
    const last = new Date(year, month + 1, 0);
    const days = [];
    const startDay = first.getDay();
    for (let i = 0; i < startDay; i++) days.push(null);
    for (let d = 1; d <= last.getDate(); d++) days.push(d);
    return days;
  };
  const getTileStatus = (year, month, day) => {
    if (day == null) return null;
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const raw = calendarData[dateStr];
    if (raw === undefined) return null;
    const qty = Math.max(0, Number(raw));
    // Compare against total stock (actual), not current in-hand quantity
    const total = product ? Math.max(Number(product.actual_quantity) || 0, 1) : 1;
    if (qty === 0) return 'booked';
    if (qty < total) return 'partial';
    return 'available';
  };
  const getTileQty = (year, month, day) => {
    if (day == null) return null;
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const raw = calendarData[dateStr];
    return raw === undefined ? null : Math.max(0, Number(raw));
  };
  const isPastDate = (year, month, day) => {
    const d = new Date(year, month, day);
    const today = new Date();
    d.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    return d < today;
  };

  if (loading) {
    return <PageLoader message="Loading product details..." />;
  }

  if (!product) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <Typography color="text.secondary">Product not found.</Typography>
      </Box>
    );
  }

  // Status reflects inventory capacity (actual), not in-hand (current).
  // current_quantity can be 0 while dispatched, but product is still bookable later.
  const hasInventory = (product.actual_quantity || 0) > 0;
  const isActive = product.is_active !== false;
  const productStatus = !isActive
    ? { label: 'Inactive', textColor: 'error.main' }
    : hasInventory
      ? { label: 'In Stock', textColor: 'success.main' }
      : { label: 'Out of Stock', textColor: 'text.secondary' };
  const days = getDaysInMonth(calendarMonth.year, calendarMonth.month);

  return (
    <Box sx={{ ...pageContainerSx, maxWidth: 1200, p: { xs: 0, sm: 2, md: 3 } }}>
      <Breadcrumb items={[{ label: 'Products', path: productsReturnPath }, { label: product.name, path: null }]} />

      <PageHeader
        title={product.name}
        subtitle={product.product_code ? `Product code: ${product.product_code}` : 'Product details and availability'}
        actionLabel="Edit Product"
        actionIcon={<EditIcon sx={{ fontSize: 16 }} />}
        onAction={openEditModal}
      />

      {/* Date filter - hidden for warehouse (no metrics) */}
      {!isWarehouse && (
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, justifyContent: 'space-between', gap: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Metrics and booking history reflect the selected date range.
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<CalendarMonthIcon sx={{ fontSize: 18 }} />}
            onClick={(e) => setDateAnchor(e.currentTarget)}
            sx={{ textTransform: 'none', fontSize: '0.8125rem', fontWeight: 400 }}
          >
            {formatDateRangeLabel(startDate, endDate)}
          </Button>
          <Popover
            open={Boolean(dateAnchor)}
            anchorEl={dateAnchor}
            onClose={() => setDateAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 260 }}>
              <TextField
                size="small"
                label="From"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                fullWidth
              />
              <TextField
                size="small"
                label="To"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                fullWidth
              />
              <Button size="small" variant="contained" onClick={() => setDateAnchor(null)}>Apply</Button>
            </Box>
          </Popover>
        </Box>
      )}

      {/* Stats cards - hidden for warehouse */}
      {!isWarehouse && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }, gap: 2 }}>
          {STAT_CARDS.map(({ key, label, suffix = '', icon: Icon }) => (
            <Card
              key={key}
              elevation={0}
              sx={{
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                p: 2,
                position: 'relative',
              }}
            >
              <Icon sx={{ color: 'text.secondary', fontSize: 28, mb: 1 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
                {stats ? (stats[key] ?? 0) : '—'}{suffix}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                {label}
              </Typography>
            </Card>
          ))}
        </Box>
      )}

      {/* Product Info + Availability Calendar (two columns) */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, gap: 2, alignItems: 'stretch' }}>
        {/* Product card - image left, details right */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
          }}
        >
          <Box
            sx={{
              flexShrink: 0,
              width: { xs: '100%', sm: 176 },
              height: { xs: 220, sm: 176 },
              bgcolor: 'grey.200',
              position: 'relative',
            }}
          >
            {product.image_url ? (
              <Box
                component="img"
                src={resolveImageUrl(product.image_url)}
                alt={product.name}
                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary', fontSize: '0.875rem' }}>
                No image
              </Box>
            )}
          </Box>
          <CardContent sx={{ flex: 1, p: 2.5, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 2 }}>
              <Typography variant="h6" component="h2" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '1rem' }}>
                {product.name}
              </Typography>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" sx={{ fontWeight: 500, color: productStatus.textColor, fontSize: '11px' }}>
                {productStatus.label}
              </Typography>
              <IconButton
                size="small"
                onClick={openEditModal}
                sx={{
                  width: 32,
                  height: 32,
                  color: 'text.secondary',
                  '&:hover': { bgcolor: 'action.hover', color: 'primary.main' },
                }}
                title="Edit product"
              >
                <EditIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
              <InfoRow icon={TagIcon} label="Product Code" value={product.product_code} />
              <InfoRow icon={CategoryIcon} label="Category" value={product.category} />
              <InfoRow icon={DescriptionIcon} label="Description" value={product.description} />
              <InfoRow icon={Inventory2OutlinedIcon} label="Current Quantity" value={String(product.current_quantity)} />
              <InfoRow icon={LocalShippingOutlinedIcon} label="Min. Order" value="1 unit" />
            </Box>
          </CardContent>
        </Card>

        {/* Availability Calendar - right column, shows Booked / Partial / Available */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          <Typography sx={{ fontSize: '11px', fontWeight: 600, color: 'text.primary', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 2 }}>
            Availability
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <IconButton size="small" sx={{ minWidth: 24, width: 24, height: 24 }} onClick={() => setCalendarMonth(m => (m.month === 0 ? { ...m, year: m.year - 1, month: 11 } : { ...m, month: m.month - 1 }))}>
              <ChevronLeftIcon sx={{ fontSize: 14 }} />
            </IconButton>
            <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '12px', color: 'text.primary' }}>
              {monthNames[calendarMonth.month]} {calendarMonth.year}
            </Typography>
            <IconButton size="small" sx={{ minWidth: 24, width: 24, height: 24 }} onClick={() => setCalendarMonth(m => (m.month === 11 ? { ...m, year: m.year + 1, month: 0 } : { ...m, month: m.month + 1 }))}>
              <ChevronRightIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.25, flex: 1, minHeight: 0 }}>
            {dayLabels.map(day => (
              <Typography key={day} variant="caption" sx={{ textAlign: 'center', fontWeight: 600, color: 'text.secondary', fontSize: '9px', py: 0.5 }}>
                {day}
              </Typography>
            ))}
            {days.map((day, i) => {
              const past = day != null && isPastDate(calendarMonth.year, calendarMonth.month, day);
              const status = !past && day != null ? getTileStatus(calendarMonth.year, calendarMonth.month, day) : null;
              const qty = day != null ? getTileQty(calendarMonth.year, calendarMonth.month, day) : null;
              const showCount = !past && qty !== undefined && qty !== null;
              return (
                <Box
                  key={i}
                  sx={{
                    aspectRatio: '1',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 0.75,
                    fontSize: '10px',
                    fontWeight: 500,
                    fontVariantNumeric: 'tabular-nums',
                    ...(past && { color: 'text.disabled', bgcolor: 'grey.100' }),
                    ...(!past && status === 'booked' && { bgcolor: 'background.paper', color: 'error.main', border: '1px solid', borderColor: 'divider' }),
                    ...(!past && status === 'partial' && { bgcolor: 'action.hover', color: 'text.primary', border: '1px solid', borderColor: 'divider' }),
                    ...(!past && status === 'available' && { bgcolor: 'background.paper', color: 'text.primary', border: '1px solid', borderColor: 'divider' }),
                    ...(!past && !status && { color: 'text.secondary', opacity: 0.5 }),
                  }}
                >
                  {day}
                  {showCount && (
                    <Box
                      component="span"
                      sx={{
                        fontSize: '11px',
                        fontWeight: 600,
                        color: status === 'booked' ? 'error.main' : 'success.main',
                        lineHeight: 1.2,
                        mt: 0.25,
                      }}
                    >
                      {Math.max(0, Number(qty))}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
          {/* Legend */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider', flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: 0.5, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }} />
              <Typography variant="caption" sx={{ fontSize: '9px', color: 'text.secondary' }}>Booked</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider' }} />
              <Typography variant="caption" sx={{ fontSize: '9px', color: 'text.secondary' }}>Partial</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: 0.5, border: '1px solid', borderColor: 'divider' }} />
              <Typography variant="caption" sx={{ fontSize: '9px', color: 'text.secondary' }}>Available</Typography>
            </Box>
          </Box>
        </Card>
      </Box>

      {/* Booking History - hidden for warehouse */}
      {!isWarehouse && (
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'primary.main' }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
            Booking History
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
            ({formatDateRangeLabel(startDate, endDate)})
          </Typography>
        </Box>
        {isMobile ? (
          <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {bookingHistory.length === 0 ? (
              <Typography sx={{ py: 4, textAlign: 'center', color: 'text.secondary', fontSize: '0.875rem' }}>No bookings in this date range.</Typography>
            ) : (
              bookingHistory.map((booking, idx) => (
                <Paper key={idx} elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
                    <Box>
                      <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'text.primary' }}>#{booking.booking_id ?? booking.id ?? '—'}</Typography>
                      <Typography sx={{ fontSize: '0.75rem', color: 'text.primary' }}>{booking.customer_name ?? '—'}</Typography>
                      <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Book: {formatDisplayDate(booking.booking_date) || '—'} · Dispatch: {formatDisplayDate(booking.dispatch_date) || '—'} · Return: {formatDisplayDate(booking.return_date) || '—'}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontSize: '0.8125rem', fontWeight: 500, color: 'text.primary' }}>Qty {booking.quantity ?? '—'}</Typography>
                      <Box component="span" sx={{ display: 'inline-block', px: 1.25, py: 0.5, borderRadius: '9999px', fontSize: '0.7rem', fontWeight: 500, bgcolor: (booking.status === 'DISPATCHED' && 'rgba(59, 130, 246, 0.12)') || (booking.status === 'RECEIVED' && 'rgba(34, 197, 94, 0.12)') || 'rgba(107, 114, 128, 0.15)', color: (booking.status === 'DISPATCHED' && '#1d4ed8') || (booking.status === 'RECEIVED' && '#15803d') || '#4b5563' }}>
                        {booking.status ?? 'ORDERED'}
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              ))
            )}
          </Box>
        ) : (
          <TableContainer>
            <Table size="small" sx={{ minWidth: 500 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell sx={{ py: 1.25, px: 2, fontSize: '10px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary' }}>Order ID</TableCell>
                  <TableCell sx={{ py: 1.25, px: 2, fontSize: '10px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary' }}>Customer</TableCell>
                  <TableCell sx={{ py: 1.25, px: 2, fontSize: '10px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary' }}>Booking</TableCell>
                  <TableCell sx={{ py: 1.25, px: 2, fontSize: '10px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary' }}>Dispatch</TableCell>
                  <TableCell sx={{ py: 1.25, px: 2, fontSize: '10px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary' }}>Return</TableCell>
                  <TableCell align="right" sx={{ py: 1.25, px: 2, fontSize: '10px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary' }}>Qty</TableCell>
                  <TableCell sx={{ py: 1.25, px: 2, fontSize: '10px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary' }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bookingHistory.length === 0 ? (
                  <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary', fontSize: '0.875rem' }}>No bookings in this date range.</TableCell></TableRow>
                ) : (
                  bookingHistory.map((booking, idx) => (
                    <TableRow key={idx} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                      <TableCell sx={{ py: 1.5, px: 2, fontSize: '0.8125rem', color: 'text.primary' }}>#{booking.booking_id ?? booking.id ?? '—'}</TableCell>
                      <TableCell sx={{ py: 1.5, px: 2, fontSize: '0.8125rem', color: 'text.primary' }}>{booking.customer_name ?? '—'}</TableCell>
                      <TableCell sx={{ py: 1.5, px: 2, fontSize: '0.8125rem', color: 'text.secondary' }}>{formatDisplayDate(booking.booking_date) || '—'}</TableCell>
                      <TableCell sx={{ py: 1.5, px: 2, fontSize: '0.8125rem', color: 'text.secondary' }}>{formatDisplayDate(booking.dispatch_date) || '—'}</TableCell>
                      <TableCell sx={{ py: 1.5, px: 2, fontSize: '0.8125rem', color: 'text.secondary' }}>{formatDisplayDate(booking.return_date) || '—'}</TableCell>
                      <TableCell align="right" sx={{ py: 1.5, px: 2, fontSize: '0.8125rem', color: 'text.primary' }}>{booking.quantity ?? '—'}</TableCell>
                      <TableCell sx={{ py: 1.5, px: 2 }}>
                        <Box component="span" sx={{ display: 'inline-block', px: 1.25, py: 0.5, borderRadius: '9999px', fontSize: '0.7rem', fontWeight: 500, bgcolor: (booking.status === 'DISPATCHED' && 'rgba(59, 130, 246, 0.12)') || (booking.status === 'RECEIVED' && 'rgba(34, 197, 94, 0.12)') || 'rgba(107, 114, 128, 0.15)', color: (booking.status === 'DISPATCHED' && '#1d4ed8') || (booking.status === 'RECEIVED' && '#15803d') || '#4b5563' }}>{booking.status ?? 'ORDERED'}</Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
      )}

      {editModalOpen && product && (
        <ProductForm
          editingProduct={product}
          onAdd={() => { fetchProduct(); fetchStats(); fetchBookingHistory(); }}
          closeModal={closeEditModal}
        />
      )}
    </Box>
  );
};

export default ProductDetails;
